import { Pill } from "@/components/atoms/Pill/Pill";
import { useSafeClickHandler } from "@/components/hooks/useSafeClickHandler";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import "./Tag.scss";

type TagProps = {
  label: string;
  onIconClick?: (selected: boolean) => void;
  iconName?: string;
  onClick?: () => void;
  onRename?: (newName: string) => void;
  isEditMode?: boolean;
  isSelected?: boolean;
  isSelectable?: boolean;
  isDestructive?: boolean;
  onCancelEdit?: () => void;
  preventClick?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

export const Tag: React.FC<TagProps> = ({
  onClick,
  onIconClick,
  label,
  iconName,
  onRename,
  isEditMode,
  onCancelEdit,
  isDestructive,
  className,
  style,
  isSelectable = true,
  preventClick = false,
  ..._rest
}) => {
  const [internalEditing, setInternalEditing] = useState(false);
  const [isSelected, setSelected] = useState(_rest.isSelected || false);
  const [value, setValue] = useState(label);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const committingRef = useRef(false);
  const { onClick: onClickSafe, onDoubleClick: onDoubleClickSafe } = useSafeClickHandler({
    onClick: () => handleClick(),
    delay: 300, // Double click threshold
  });
  const destructiveClass = isDestructive ? "tag__icon--destructive" : "";
  const editing = isEditMode !== undefined ? isEditMode : internalEditing;

  // Keep local value in sync if label changes while not editing
  useEffect(() => {
    if (!editing) {
      setValue(label);
    }
  }, [label, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      const el = inputRef.current;
      el.focus();
      // Select all text after focusing
      try {
        el.setSelectionRange(0, el.value.length);
      } catch {
        console.warn("Text selection is outside range");
      }
    }
  }, [editing]);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (!editing) {
        return;
      }
      // Only primary left click/tap
      if (!event.isPrimary || event.button !== 0) {
        return;
      }
      const input = inputRef.current;
      if (input && event.target instanceof Node && input.contains(event.target)) {
        return;
      }
      commitRename();
    };

    document.addEventListener("pointerdown", handlePointerDown, { capture: true });
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, { capture: true });
    };
  }, [editing]);

  const startEditing = () => {
    if (!onRename) {
      return;
    }
    setValue(label);
    if (isEditMode === undefined) {
      setInternalEditing(true);
    }
  };

  const handleClick = () => {
    if (preventClick || editing) {
      return;
    }

    if (isSelectable) {
      setSelected(!isSelected);
    }

    if (onClick) {
      onClick();
    }
  };

  const cancelEditing = () => {
    if (isEditMode === undefined) {
      setInternalEditing(false);
    }
    setValue(label);
  };

  const commitRename = () => {
    if (committingRef.current) {
      return;
    }
    committingRef.current = true;
    const raw = inputRef.current ? inputRef.current.value : value;
    const next = (raw ?? "").trim();
    if (!next) {
      cancelEditing();
    } else {
      if (onRename && next.length > 0) {
        onRename(next);
      }
      if (isEditMode === undefined) {
        setInternalEditing(false);
      }
    }
    setTimeout(() => {
      committingRef.current = false;
    }, 0);
  };

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitRename();
    } else if (e.key === "Escape") {
      e.preventDefault();
      if (onCancelEdit) {
        onCancelEdit();
      } else {
        cancelEditing();
      }
    }
  };

  return (
    <div className={className} style={style}>
      <Pill
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onClickSafe(e);
        }}
        className="tag-style"
        isSelected={isSelected}
        onDoubleClick={(e) => {
          onDoubleClickSafe(e);
          startEditing();
        }}
      >
        {editing ? (
          <input
            ref={inputRef}
            className="tag__input"
            type="text"
            maxLength={23}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label={`Rename ${label}`}
            placeholder={"Type a new name"}
          />
        ) : (
          <>
            <span className="tag__label">{label}</span>
            {iconName && (
              <span
                className={`tag__icon ${destructiveClass} material-icons-outlined${
                  onIconClick ? " tag__icon--clickable" : ""
                }`}
                aria-label={onIconClick ? `Action for ${label}` : undefined}
                role={onIconClick ? "button" : undefined}
                tabIndex={onIconClick ? 0 : -1}
                onClick={
                  onIconClick
                    ? (e) => {
                        e.stopPropagation();
                        onIconClick?.(isSelected);
                      }
                    : undefined
                }
                onKeyDown={
                  onIconClick
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          onIconClick?.(isSelected);
                        }
                      }
                    : undefined
                }
              >
                {iconName}
              </span>
            )}
          </>
        )}
      </Pill>
    </div>
  );
};
